import { useEffect } from 'react';
import { createRootRoute, Outlet, useRouterState } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { MobileStickyBar } from '@/components/sections/mobile-sticky-bar';
import { SmsFAB } from '@/components/sections/sms-fab';
import { BackToTop } from '@/components/sections/back-to-top';
import { CalendlyModal } from '@/components/sections/calendly-modal';
import { CarnetModal } from '@/components/sections/carnet-modal';
import { LexiqueModal } from '@/components/sections/lexique-modal';
import { ColophonModal } from '@/components/sections/colophon-modal';
import { ExitIntentPopup } from '@/components/sections/exit-intent-popup';
import { NotFoundPage } from '@/components/sections/not-found-page';
import { MouseSpotlight } from '@/components/ui/mouse-spotlight';
import { ScrollProgress } from '@/components/layout/scroll-progress';
import { ScrollSideLine } from '@/components/layout/scroll-side-line';
import { IntralysWidget } from '@/components/layout/IntralysWidget';
import { LeadModalProvider } from '@/lib/LeadModalContext';
import { useLenis, getLenis, scrollToHash } from '@/lib/hooks/use-lenis';

function RootLayout() {
  useLenis(); // Phase 27.1 — butter-smooth scroll (desktop only, prefers-reduced-motion opt-out)

  // Phase 27.73 fix 2 — preload des 5 sections lazy au idle. Évite le freeze
  // "click → load chunk → render" quand user scroll-jump (via SectionRail ou
  // Navbar hash link) vers une section non-encore loaded.
  // requestIdleCallback (avec fallback setTimeout) trigger les dynamic imports
  // après idle initial, sans bloquer le critical path / LCP.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const preload = () => {
      void import('@/components/sections/comparator-collectif-amg');
      void import('@/components/sections/diagnostic-quiz-5q');
      void import('@/components/sections/calculatrice-amg');
      void import('@/components/sections/premium-estimator');
      void import('@/components/sections/contact-form');
    };
    const win = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (typeof win.requestIdleCallback === 'function') {
      const id = win.requestIdleCallback(preload, { timeout: 3000 });
      return () => {
        if (typeof win.cancelIdleCallback === 'function') {
          win.cancelIdleCallback(id);
        }
      };
    }
    const id = window.setTimeout(preload, 2000);
    return () => window.clearTimeout(id);
  }, []);

  // Phase 27.46 — port pattern Buteau v55 (cross-Intralys) :
  // 1. Au refresh ou changement de route, force scroll-to-top si pas de hash valide
  //    (le browser sinon restaure la position pré-refresh = retour milieu de page).
  // 2. Pour les hashes valides (#contact, etc.), polling ~1.5s qui re-scroll
  //    à chaque frame jusqu'à stabilisation du layout (lazy sections, images).
  // 3. Auto-cancel si l'user interagit (wheel/touch/keydown/click hors hash anchor)
  //    pour éviter le rogue scroll qui contrarie l'intent user.
  const { location } = useRouterState();
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let cancelled = false;
    const cancel = () => {
      cancelled = true;
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        const hash = window.location.hash.slice(1);
        if (!hash || !document.getElementById(hash)) {
          const lenis = getLenis();
          if (lenis) lenis.scrollTo(0, { immediate: true });
          else window.scrollTo({ top: 0, behavior: 'instant' });
          return;
        }
        window.addEventListener('wheel', cancel, { once: true, passive: true });
        window.addEventListener('touchstart', cancel, { once: true, passive: true });
        window.addEventListener('keydown', cancel, { once: true });
        const clickCancel = (e: MouseEvent) => {
          const a = (e.target as HTMLElement | null)?.closest?.<HTMLAnchorElement>('a');
          if (a && /^\/?#/.test(a.getAttribute('href') || '')) return;
          cancel();
        };
        window.addEventListener('click', clickCancel, { once: true });
        let attempts = 0;
        const tick = () => {
          if (cancelled || attempts++ > 90) return;
          if (document.getElementById(hash)) scrollToHash(hash);
          requestAnimationFrame(tick);
        };
        scrollToHash(hash);
        requestAnimationFrame(tick);
      });
    });
    return () => {
      cancelled = true;
      window.removeEventListener('wheel', cancel);
      window.removeEventListener('touchstart', cancel);
      window.removeEventListener('keydown', cancel);
    };
  }, [location.pathname]);

  return (
    <LeadModalProvider>
      {/* Phase 27.72F — scroll progress indicators (cross-Intralys parité Buteau/EGSF/Serujan).
          ScrollProgress = fine ligne terracotta top viewport qui se remplit au scroll.
          ScrollSideLine = rail vertical desktop only avec tête lumineuse qui descend. */}
      <ScrollProgress />
      <ScrollSideLine />
      <MouseSpotlight />
      <Outlet />
      <MobileStickyBar />
      <SmsFAB />
      <BackToTop />
      <CalendlyModal />
      {/* Phase 27.81 — Pattern dual modal+page Carnet/Lexique/Colophon (cross-Intralys). */}
      <CarnetModal />
      <LexiqueModal />
      <ColophonModal />
      <ExitIntentPopup />
      {/* Widget signature Intralys flottant bottom-left (next to BackToTop).
          Cross-pages = présent sur toutes les routes (legal/lexique inclus). */}
      <IntralysWidget clientId="oceanne" category="services" />
      {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
    </LeadModalProvider>
  );
}

export const Route = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFoundPage
});
